import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { MetricsBar } from '../components/MetricsBar';
import { SearchFilterBar } from '../components/SearchFilterBar';
import { CaseGrid } from '../components/CaseGrid';

export default function TrainingLibrary() {
  const { token } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [sessions, setSessions] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        
        const [statsRes, sessionsRes, casesRes] = await Promise.all([
          axios.get(`${apiUrl}/api/v1/clinicalsim/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/api/v1/clinicalsim/sessions`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/api/v1/clinicalsim/cases`, { 
            headers: { Authorization: `Bearer ${token}` },
            params: {
              search: searchQuery || undefined,
              difficulty: difficultyFilter !== 'all' ? difficultyFilter : undefined
            }
          })
        ]);

        setStats(statsRes.data);
        setSessions(sessionsRes.data?.sessions || []);
        setCases(casesRes.data?.cases || []);
        setError('');
      } catch (err) {
        console.error('Failed to load training library data:', err);
        setError('Failed to load cases. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    // Debounce search
    const timer = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(timer);
  }, [token, searchQuery, difficultyFilter]);

  if (loading && !stats) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Training Library</h1>
        <p className="text-slate-500 mt-1">Browse simulation cases and practice your clinical skills.</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl border border-red-100">
          {error}
        </div>
      )}

      <MetricsBar stats={stats} />
      
      <SearchFilterBar 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        difficultyFilter={difficultyFilter}
        onDifficultyChange={setDifficultyFilter}
      />
      
      <CaseGrid 
        cases={cases} 
        userSessions={sessions} 
      />
    </div>
  );
}
